import React, { useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import {
  LoadingDiv,
  LoadingDivShort,
  SweetAlertShow,
} from "../../master/Master";

import Header from "../../master/SideBar";

import Nav from "../../master/NavBar";

import MasterV2 from "../MasterV2";

import {
  studentAttendanceDashboardV4,
  studentAttendanceStudentSearch,
} from "../../../api/Attendance";
import usePrivateLink from "../../config/usePrivateLink";

/* =========================================================



   REPORT ROUTES







   Change only these paths if your project uses other routes.



\\========================================================= */

const REPORT_ROUTES = {
  dayWise: "/attendance/reports/day-wise",

  monthly: "/attendance/reports/monthly",

  annual: "/attendance/reports/annual",

  manual: "/attendance/mark",

  lowAttendance: "/attendance/reports/low-attendance",

  staffWise: "/attendance/reports/staff-wise",

  messageDelivery: "/attendance/reports/message-delivery",

  studentHistory: "/attendance/reports/admission-month-wise",
  followups_calling: "/student/attendance/followups",

  longAbsent: "/attendance/reports/long-absent",
  studentReport: "/student-attendance/student",
};

/* =========================================================



   DATE HELPERS



\\========================================================= */

const formatDate = (date) => {
  const year = date.getFullYear();

  const month = String(date.getMonth() + 1).padStart(2, "0");

  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatReadableDate = (value) => {
  if (!value) return "-";

  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString("en-IN", {
      day: "2-digit",

      month: "short",

      year: "numeric",
    });
  } catch (error) {
    return value;
  }
};

/* =========================================================



   COMPONENT



\\========================================================= */

export default function StudentAttendanceDashboardv2() {
  const navigate = useNavigate();
  const hasAccess = usePrivateLink();
  const today = new Date();

  /* =========================================================



     STATE



  \\========================================================= */

  const [loading, setLoading] = useState(false);

  const [sessions, setSessions] = useState([]);

  const [data, setData] = useState({});

  const [studentReportModal, setStudentReportModal] = useState(false);
  const [studentSearch, setStudentSearch] = useState("");
  const [studentSearchLoading, setStudentSearchLoading] = useState(false);
  const [studentSearchResults, setStudentSearchResults] = useState([]);
  const [studentSearchTouched, setStudentSearchTouched] = useState(false);

  const [filters, setFilters] = useState({
    date: formatDate(today),

    session_id: "",
  });

  /* =========================================================



     LOAD SESSIONS



  \\========================================================= */

  useEffect(() => {
    try {
      const storedSessions = JSON.parse(
        localStorage.getItem("sessions") ?? "[]",
      );

      setSessions(Array.isArray(storedSessions) ? storedSessions : []);
    } catch (error) {
      console.error("Session parse error:", error);

      setSessions([]);
    }
  }, []);

  /* =========================================================



     SET ACTIVE SESSION



  \\========================================================= */

  useEffect(() => {
    if (!sessions?.length) return;

    const activeSession = sessions.find(
      (session) => Number(session?.status) === 1,
    );

    if (!activeSession?.id) return;

    setFilters((previous) => {
      if (previous.session_id) {
        return previous;
      }

      return {
        ...previous,

        session_id: activeSession.id,
      };
    });
  }, [sessions]);

  /* =========================================================



     ACTIVE SESSION



  \\========================================================= */

  const activeSession = useMemo(() => {
    return sessions.find(
      (session) => String(session?.id) === String(filters?.session_id),
    );
  }, [sessions, filters.session_id]);

  /* =========================================================



     BUILD QUERY



  \\========================================================= */

  const buildQuery = () => {
    const query = {
      session_id: filters?.session_id,

      date: filters?.date,
    };

    return Object.keys(query)

      .filter(
        (key) =>
          query[key] !== undefined && query[key] !== null && query[key] !== "",
      )

      .map((key) => `${key}=${encodeURIComponent(query[key])}`)

      .join("&");
  };

  /* =========================================================



     LOAD DASHBOARD SUMMARY



  \\========================================================= */

  const getAttendanceDashboard = async () => {
    if (!filters?.session_id) {
      return;
    }

    setLoading(true);

    try {
      const query = buildQuery();

      const response = await studentAttendanceDashboardV4(`&${query}`);

      if (response?.data?.status) {
        setData(response?.data?.data || {});
      } else {
        SweetAlertShow(
          "info",

          "Warning",

          response?.data?.message || "Unable to load attendance dashboard",
        );
      }
    } catch (error) {
      console.error("Attendance dashboard error:", error);

      SweetAlertShow(
        "error",

        "Error",

        error?.response?.data?.message ||
          error?.response?.message ||
          "Unable to load attendance dashboard",
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================



     AUTO LOAD



  \\========================================================= */

  useEffect(() => {
    if (!filters?.session_id) return;

    getAttendanceDashboard();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.session_id, filters.date]);

  /* =========================================================



     TOTALS



  \\========================================================= */

  const totalStudents = Number(data?.total || 0);

  const presentStudents = Number(data?.present || 0);

  const absentStudents = Number(data?.absent || 0);

  const notMarkedStudents = Number(data?.not_marked || 0);

  /* =========================================================



     PERCENTAGES



  \\========================================================= */

  const percentages = useMemo(() => {
    if (!totalStudents) {
      return {
        present: 0,

        absent: 0,

        notMarked: 0,
      };
    }

    return {
      present: Number(((presentStudents / totalStudents) * 100).toFixed(1)),

      absent: Number(((absentStudents / totalStudents) * 100).toFixed(1)),

      notMarked: Number(((notMarkedStudents / totalStudents) * 100).toFixed(1)),
    };
  }, [totalStudents, presentStudents, absentStudents, notMarkedStudents]);

  /* =========================================================



     NAVIGATION



  \\========================================================= */

  const openReport = (path, extraParams = {}) => {
    const params = new URLSearchParams();

    if (filters?.session_id) {
      params.set("session_id", filters.session_id);
    }

    if (filters?.date) {
      params.set("date", filters.date);
    }

    Object.entries(extraParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, value);
      }
    });

    const queryString = params.toString();

    navigate(queryString ? `${path}?${queryString}` : path);
  };

  /* =========================================================
     SINGLE STUDENT ATTENDANCE REPORT SEARCH
  ========================================================= */

  const openStudentReportSearch = () => {
    setStudentSearch("");
    setStudentSearchResults([]);
    setStudentSearchTouched(false);
    setStudentReportModal(true);
  };

  const closeStudentReportSearch = () => {
    setStudentReportModal(false);
    setStudentSearch("");
    setStudentSearchResults([]);
    setStudentSearchTouched(false);
  };

  const searchStudentsForReport = async (keyword = studentSearch) => {
    const value = String(keyword || "").trim();

    if (value.length < 2) {
      setStudentSearchResults([]);
      setStudentSearchTouched(Boolean(value));
      return;
    }

    setStudentSearchLoading(true);
    setStudentSearchTouched(true);

    try {
      const params = new URLSearchParams();
      params.set("search", value);
      params.set("limit", "20");

      if (filters?.session_id) {
        params.set("session_id", filters.session_id);
      }

      const response = await studentAttendanceStudentSearch(
        `&${params.toString()}`,
      );

      if (response?.data?.status) {
        const result = response?.data?.data;
        setStudentSearchResults(
          Array.isArray(result)
            ? result
            : Array.isArray(result?.data)
              ? result.data
              : [],
        );
      } else {
        setStudentSearchResults([]);
      }
    } catch (error) {
      console.error("Student attendance search error:", error);
      setStudentSearchResults([]);

      SweetAlertShow(
        "error",
        "Search Error",
        error?.response?.data?.message ||
          "Unable to search students. Please try again.",
      );
    } finally {
      setStudentSearchLoading(false);
    }
  };

  useEffect(() => {
    if (!studentReportModal) return;

    const value = studentSearch.trim();

    if (value.length < 2) {
      setStudentSearchResults([]);
      setStudentSearchTouched(Boolean(value));
      return;
    }

    const timer = setTimeout(() => {
      searchStudentsForReport(value);
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentSearch, studentReportModal, filters.session_id]);

  const openSingleStudentAttendanceReport = (student) => {
    const studentId = student?.id || student?.student_id;

    if (!studentId) {
      SweetAlertShow(
        "info",
        "Student Not Found",
        "Student ID is missing for the selected student.",
      );
      return;
    }

    closeStudentReportSearch();
    navigate(`${REPORT_ROUTES.studentReport}/${studentId}/report`);
  };

  const studentRegNo = (student) =>
    student?.registration_no ||
    student?.registration_number ||
    student?.admission_no ||
    student?.scholar_no ||
    "-";

  const studentBatchName = (student) =>
    student?.course_and_fee_info?.batch?.title ||
    student?.latest_course_and_fee?.batch?.title ||
    student?.batch?.title ||
    student?.batch_title ||
    "-";

  const studentCourseName = (student) =>
    student?.course_and_fee_info?.course?.title ||
    student?.latest_course_and_fee?.course?.title ||
    student?.course?.title ||
    student?.course_title ||
    "-";

  /* =========================================================



     SUMMARY CARDS



  \\========================================================= */

  const summaryCards = [
    {
      key: "total",

      title: "Total Enrolled",

      subtitle: "Active students",

      value: totalStudents,

      icon: "ri-group-line",

      className: "attendance-stat-primary",

      percentage: null,
    },

    {
      key: "present",

      title: "Present",

      subtitle: "Students present today",

      value: presentStudents,

      icon: "ri-checkbox-circle-line",

      className: "attendance-stat-success",

      percentage: percentages.present,

      onClick: () =>
        openReport(REPORT_ROUTES.dayWise, {
          attendance: "1",
        }),
    },

    {
      key: "absent",

      title: "Absent",

      subtitle: "Students absent today",

      value: absentStudents,

      icon: "ri-close-circle-line",

      className: "attendance-stat-danger",

      percentage: percentages.absent,

      onClick: () =>
        openReport(REPORT_ROUTES.dayWise, {
          attendance: "2",
        }),
    },

    {
      key: "notMarked",

      title: "Not Marked",

      subtitle: "Attendance still pending",

      value: notMarkedStudents,

      icon: "ri-time-line",

      className: "attendance-stat-warning",

      percentage: percentages.notMarked,

      onClick: () =>
        openReport(REPORT_ROUTES.dayWise, {
          attendance: "0",
        }),
    },
  ];

  /* =========================================================



     REPORT CARDS



  \\========================================================= */

  const primaryReports = [
    {
      id: "day-wise",
      path: REPORT_ROUTES.dayWise,
      title: "Day Wise Attendance",
      description:
        "View attendance batch wise for any selected date with present, absent and pending students.",
      icon: "ri-calendar-check-line",
      iconClass: "report-icon-blue",
      tag: "Daily",
      onClick: () => openReport(REPORT_ROUTES.dayWise),
    },
    {
      id: "monthly",
      path: REPORT_ROUTES.monthly,
      title: "Monthly Attendance Register",
      description:
        "Complete monthly register with student-wise daily attendance, totals and percentages.",
      icon: "ri-calendar-2-line",
      iconClass: "report-icon-purple",
      tag: "Register",
      onClick: () =>
        openReport(REPORT_ROUTES.monthly, {
          month: filters.date?.substring(0, 7),
        }),
    },
    {
      id: "annual",
      path: REPORT_ROUTES.annual,
      title: "Annual Attendance Register",
      description:
        "View yearly attendance performance with month-wise and student-wise summaries.",
      icon: "ri-calendar-todo-line",
      iconClass: "report-icon-indigo",
      tag: "Annual",
      onClick: () =>
        openReport(REPORT_ROUTES.annual, {
          year: filters.date?.substring(0, 4),
        }),
    },
    {
      id: "manual",
      path: REPORT_ROUTES.manual,
      title: "Mark Attendance Manually",
      description:
        "Select batch and date to mark, correct or update student attendance manually.",
      icon: "ri-edit-box-line",
      iconClass: "report-icon-green",
      tag: "Action",
      onClick: () => openReport(REPORT_ROUTES.manual),
    },
  ];

  const analysisReports = [
    {
      id: "single-student-attendance-report",
      path: REPORT_ROUTES.studentReport,
      title: "Student Attendance Report",
      description:
        "Search any student by name, registration number or mobile and open the complete individual attendance report.",
      icon: "ri-user-search-line",
      iconClass: "report-icon-cyan",
      tag: "Student",
      onClick: openStudentReportSearch,
    },
    {
      id: "low-attendance",
      path: REPORT_ROUTES.lowAttendance,
      title: "Low Attendance Report",
      description:
        "Identify students below the required attendance percentage such as 75%, 60% or custom limits.",
      icon: "ri-arrow-down-circle-line",
      iconClass: "report-icon-red",
      tag: "Important",
      onClick: () => openReport(REPORT_ROUTES.lowAttendance),
    },

    // {
    //   id: "staff-wise",
    //   path: REPORT_ROUTES.staffWise,
    //   title: "Staff Wise Attendance",
    //   description:
    //     "Analyze attendance performance for batches and students managed by each staff member.",
    //   icon: "ri-user-settings-line",
    //   iconClass: "report-icon-cyan",
    //   tag: "Staff",
    //   onClick: () => openReport(REPORT_ROUTES.staffWise),
    // },

    {
      id: "message",
      path: REPORT_ROUTES.messageDelivery,
      title: "Message Delivery Report",
      description:
        "Track attendance Push and WhatsApp messages including sent, pending and failed delivery.",
      icon: "ri-message-3-line",
      iconClass: "report-icon-orange",
      tag: "Messages",
      onClick: () => openReport(REPORT_ROUTES.messageDelivery),
    },
    {
      id: "admission-month-wise-attendance-report",
      path: REPORT_ROUTES.studentHistory,
      title: "Admission Month Wise Attendance Report",
      description:
        "Monthly attendance report for students admitted in a specific month.",
      icon: "ri-user-search-line",
      iconClass: "report-icon-violet",
      tag: "Student",
      onClick: () => openReport(REPORT_ROUTES.studentHistory),
    },
    {
      id: "calling-followups",
      path: REPORT_ROUTES.followups_calling,
      title: "Calling Follow-ups",
      description:
        "Find students continuously absent for multiple days and identify cases requiring follow-up.",
      icon: "ri-user-unfollow-line",
      iconClass: "report-icon-yellow",
      tag: "Follow-up",
      onClick: () => openReport(REPORT_ROUTES.followups_calling),
    },
  ];

  const visiblePrimaryReports = primaryReports.filter((report) =>
    hasAccess(report.path),
  );

  const visibleAnalysisReports = analysisReports.filter((report) =>
    hasAccess(report.path),
  );

  /* =========================================================



     REPORT CARD COMPONENT



  \\========================================================= */

  const ReportCard = ({ report }) => (
    <button
      type="button"
      className="attendance-report-card"
      onClick={report.onClick}
    >
      <div className="attendance-report-card-top">
        <div className={`attendance-report-icon ${report.iconClass}`}>
          <i className={report.icon}></i>
        </div>

        <span className="attendance-report-tag">{report.tag}</span>
      </div>

      <div className="attendance-report-content">
        <h5>{report.title}</h5>

        <p>{report.description}</p>
      </div>

      <div className="attendance-report-footer">
        <span>Open Report</span>

        <div className="attendance-report-arrow">
          <i className="ri-arrow-right-line"></i>
        </div>
      </div>
    </button>
  );

  /* =========================================================



     RENDER



  \\========================================================= */

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar attendance-dashboard">
        <div className="layout-container">
          {/* SIDEBAR - same structure as old attendance dashboard */}

          <div className="no-print">
            <Header />
          </div>

          <div className="layout-page">
            {/* TOP HEADER / NAVBAR */}

            <div className="no-print">
              <Nav />
            </div>

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                {/* ATTENDANCE V2 MASTER MENU */}

                <div className="no-print">
                  <MasterV2 />
                </div>

                <div className="attendance-reporting-page">
                  <main className="">
                    {/* ===================================================



              PAGE HEADER



          \\=================================================== */}

                    {/* <section className="attendance-page-header mt-4">

                      <div className="attendance-header-left">

                        <div className="attendance-header-icon">

                          <i className="ri-calendar-check-line"></i>

                        </div>



                        <div>

                          <div className="attendance-breadcrumb">

                            Student Attendance

                            <i className="ri-arrow-right-s-line"></i>

                            Reporting

                          </div>



                          <h1>Attendance Reporting Center</h1>



                          <p>

                            Monitor student attendance and access all attendance

                            reports from one place.

                          </p>

                        </div>

                      </div>



                      <div className="attendance-header-actions">

                        <button

                          type="button"

                          className="attendance-refresh-btn"

                          onClick={getAttendanceDashboard}

                          disabled={loading}

                        >

                          <i

                            className={

                              loading

                                ? "ri-loader-4-line attendance-spin"

                                : "ri-refresh-line"

                            }

                          ></i>



                          <span>Refresh</span>

                        </button>



                        <button

                          type="button"

                          className="attendance-mark-btn"

                          onClick={() => openReport(REPORT_ROUTES.manual)}

                        >

                          <i className="ri-add-circle-line"></i>



                          <span>Mark Attendance</span>

                        </button>

                      </div>

                    </section> */}

                    {/* ===================================================



              FILTER BAR



          \\=================================================== */}

                    {/* <section className="attendance-filter-card mt-2">

                      <div className="attendance-filter-heading">

                        <div className="attendance-filter-icon">

                          <i className="ri-equalizer-2-line"></i>

                        </div>



                        <div>

                          <strong>Dashboard View</strong>



                          <small>

                            Select session and date for today's overview

                          </small>

                        </div>

                      </div>



                      <div className="attendance-filter-controls">

                        <div className="attendance-filter-field">

                          <label>Academic Session</label>



                          <div className="attendance-input-wrapper">

                            <i className="ri-graduation-cap-line"></i>



                            <select

                              value={filters?.session_id || ""}

                              onChange={(e) =>

                                setFilters((previous) => ({

                                  ...previous,



                                  session_id: e.target.value,

                                }))

                              }

                            >

                              <option value="">Select Session</option>



                              {sessions.map((session) => (

                                <option key={session?.id} value={session?.id}>

                                  {session?.session || "Session"}



                                  {Number(session?.status) === 1

                                    ? " - Active"

                                    : ""}

                                </option>

                              ))}

                            </select>



                          </div>

                        </div>



                        <div className="attendance-filter-field">

                          <label>Attendance Date</label>



                          <div className="attendance-input-wrapper">

                            <i className="ri-calendar-line"></i>



                            <input

                              type="date"

                              value={filters?.date || ""}

                              onChange={(e) =>

                                setFilters((previous) => ({

                                  ...previous,



                                  date: e.target.value,

                                }))

                              }

                            />

                          </div>

                        </div>

                      </div>

                    </section> */}

                    {/* ===================================================



              TODAY LABEL



          \\=================================================== */}

                    <section className="attendance-section-heading">
                      <div>
                        <span className="attendance-heading-icon">
                          <i className="ri-pulse-line"></i>
                        </span>

                        <div>
                          <h2>Attendance Overview</h2>

                          <p>
                            {formatReadableDate(filters?.date)}

                            {activeSession?.session
                              ? ` • ${activeSession.session}`
                              : ""}
                          </p>
                        </div>
                      </div>

                      <span className="attendance-live-badge">
                        <span></span>
                        Daily Summary
                      </span>

                      <div className="attendance-filter-controls">
                        <div className="attendance-filter-field">
                          <label>Academic Session</label>

                          <div className="attendance-input-wrapper">
                            <i className="ri-graduation-cap-line"></i>

                            <select
                              value={filters?.session_id || ""}
                              onChange={(e) =>
                                setFilters((previous) => ({
                                  ...previous,

                                  session_id: e.target.value,
                                }))
                              }
                            >
                              <option value="">Select Session</option>

                              {sessions.map((session) => (
                                <option key={session?.id} value={session?.id}>
                                  {session?.session || "Session"}

                                  {Number(session?.status) === 1
                                    ? " - Active"
                                    : ""}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="attendance-filter-field">
                          <label>Attendance Date</label>

                          <div className="attendance-input-wrapper">
                            <i className="ri-calendar-line"></i>

                            <input
                              type="date"
                              value={filters?.date || ""}
                              onChange={(e) =>
                                setFilters((previous) => ({
                                  ...previous,

                                  date: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* ===================================================



              SUMMARY CARDS



          \\=================================================== */}

                    <section className="attendance-summary-grid">
                      {summaryCards.map((item) => (
                        <button
                          type="button"
                          key={item.key}
                          className={`attendance-stat-card ${item.className}`}
                          onClick={item.onClick}
                          disabled={!item.onClick}
                        >
                          <div className="attendance-stat-card-head">
                            <div className="attendance-stat-icon">
                              <i className={item.icon}></i>
                            </div>

                            {item.percentage !== null && (
                              <span className="attendance-stat-percent">
                                {item.percentage}%
                              </span>
                            )}
                          </div>

                          <div className="attendance-stat-value">
                            {loading ? <LoadingDivShort /> : item.value}
                          </div>

                          <div className="attendance-stat-title">
                            {item.title}
                          </div>

                          <div className="attendance-stat-subtitle">
                            {item.subtitle}
                          </div>

                          {item.percentage !== null && (
                            <div className="attendance-stat-progress">
                              <div
                                style={{
                                  width: `${Math.min(
                                    100,

                                    Math.max(0, item.percentage),
                                  )}%`,
                                }}
                              ></div>
                            </div>
                          )}
                        </button>
                      ))}
                    </section>

                    {/* ===================================================



              PRIMARY REPORTS



          \\=================================================== */}

                    <section className="attendance-reports-section">
                      <div className="attendance-section-title">
                        <div>
                          <div className="attendance-section-title-icon blue">
                            <i className="ri-file-chart-line"></i>
                          </div>

                          <div>
                            <h2>Attendance Registers</h2>

                            <p>
                              Daily, monthly and annual attendance reporting
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="attendance-report-grid primary-grid">
                        {visiblePrimaryReports.map((report) => (
                          <ReportCard key={report.id} report={report} />
                        ))}
                      </div>
                    </section>

                    {/* ===================================================



              ANALYTICS / MANAGEMENT REPORTS



          \\=================================================== */}

                    <section className="attendance-reports-section">
                      <div className="attendance-section-title">
                        <div>
                          <div className="attendance-section-title-icon purple">
                            <i className="ri-bar-chart-box-line"></i>
                          </div>

                          <div>
                            <h2>Analysis & Monitoring</h2>

                            <p>
                              Reports for attendance monitoring and follow-up
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="attendance-report-grid">
                        {visibleAnalysisReports.map((report) => (
                          <ReportCard key={report.id} report={report} />
                        ))}
                      </div>
                    </section>

                    {/* ===================================================



              QUICK ACTION BOTTOM



          \\=================================================== */}

                    <section className="attendance-bottom-banner">
                      <div className="attendance-bottom-banner-icon">
                        <i className="ri-edit-circle-line"></i>
                      </div>

                      <div className="attendance-bottom-banner-content">
                        <h4>Need to update today's attendance?</h4>

                        <p>
                          Open manual attendance to mark missing attendance or
                          correct previously entered records.
                        </p>
                      </div>

                      {hasAccess(REPORT_ROUTES.manual) && (
                        <button
                          type="button"
                          onClick={() => openReport(REPORT_ROUTES.manual)}
                        >
                          Mark / Edit Attendance
                          <i className="ri-arrow-right-line"></i>
                        </button>
                      )}
                    </section>
                  </main>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SINGLE STUDENT ATTENDANCE REPORT SEARCH MODAL */}
      {studentReportModal && (
        <div
          className="student-report-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeStudentReportSearch();
            }
          }}
        >
          <div
            className="student-report-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="student-report-modal-title"
          >
            <div className="student-report-modal-header">
              <div className="student-report-modal-title">
                <span>
                  <i className="ri-user-search-line"></i>
                </span>

                <div>
                  <h3 id="student-report-modal-title">
                    Student Attendance Report
                  </h3>
                  <p>
                    Search a student and open the complete attendance report.
                  </p>
                </div>
              </div>

              <button
                type="button"
                className="student-report-modal-close"
                onClick={closeStudentReportSearch}
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="student-report-modal-body">
              <div className="student-report-session-note">
                <i className="ri-graduation-cap-line"></i>
                <span>Session:</span>
                <strong>{activeSession?.session || "Current Session"}</strong>
              </div>

              <div className="student-report-search-box">
                <i className="ri-search-line"></i>

                <input
                  autoFocus
                  type="text"
                  value={studentSearch}
                  placeholder="Search name, Reg. No. or mobile..."
                  onChange={(event) => setStudentSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") searchStudentsForReport();
                    if (event.key === "Escape") closeStudentReportSearch();
                  }}
                />

                {studentSearchLoading ? (
                  <span className="student-search-spinner">
                    <i className="ri-loader-4-line attendance-spin"></i>
                  </span>
                ) : studentSearch ? (
                  <button
                    type="button"
                    className="student-search-clear"
                    onClick={() => {
                      setStudentSearch("");
                      setStudentSearchResults([]);
                      setStudentSearchTouched(false);
                    }}
                  >
                    <i className="ri-close-circle-line"></i>
                  </button>
                ) : null}
              </div>

              <div className="student-report-search-help">
                Type at least 2 characters. Search results load automatically.
              </div>

              <div className="student-report-results">
                {studentSearchLoading ? (
                  <div className="student-report-empty">
                    <i className="ri-loader-4-line attendance-spin"></i>
                    <strong>Searching students...</strong>
                  </div>
                ) : studentSearchResults.length > 0 ? (
                  <>
                    <div className="student-result-count">
                      <strong>
                        {studentSearchResults.length} student
                        {studentSearchResults.length === 1 ? "" : "s"} found
                      </strong>
                      <span>Click student to open report</span>
                    </div>

                    <div className="student-result-list">
                      {studentSearchResults.map((student) => {
                        const studentId = student?.id || student?.student_id;
                        const mobile =
                          student?.mobile ||
                          student?.phone ||
                          student?.mobile_no ||
                          student?.father_mobile ||
                          "-";

                        return (
                          <button
                            type="button"
                            key={studentId}
                            className="student-result-item"
                            onClick={() =>
                              openSingleStudentAttendanceReport(student)
                            }
                          >
                            <div className="student-result-avatar">
                              {String(student?.name || "S")
                                .trim()
                                .split(/\s+/)
                                .slice(0, 2)
                                .map((part) => part?.[0] || "")
                                .join("")
                                .toUpperCase()}
                            </div>

                            <div className="student-result-main">
                              <div className="student-result-name-row">
                                <strong>{student?.name || "Student"}</strong>
                                <span>{studentRegNo(student)}</span>
                              </div>

                              <div className="student-result-meta">
                                <span>
                                  <i className="ri-book-open-line"></i>
                                  {studentCourseName(student)}
                                </span>
                                <span>
                                  <i className="ri-group-line"></i>
                                  {studentBatchName(student)}
                                </span>
                                <span>
                                  <i className="ri-phone-line"></i>
                                  {mobile}
                                </span>
                              </div>
                            </div>

                            <div className="student-result-open">
                              <span>View Report</span>
                              <i className="ri-arrow-right-line"></i>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </>
                ) : studentSearchTouched && studentSearch.trim().length >= 2 ? (
                  <div className="student-report-empty">
                    <i className="ri-user-search-line"></i>
                    <strong>No student found</strong>
                    <p>Try another name, registration number or mobile.</p>
                  </div>
                ) : (
                  <div className="student-report-empty">
                    <i className="ri-user-3-line"></i>
                    <strong>Find a student</strong>
                    <p>
                      Search a student to open the complete individual
                      attendance report.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================



          STYLES



      \\========================================================= */}

      <style>
        {`







 
       /* SINGLE STUDENT REPORT SEARCH */
       .student-report-modal-backdrop {
         position: fixed;
         inset: 0;
         z-index: 99999;
         display: flex;
         align-items: center;
         justify-content: center;
         padding: 20px;
         background: rgba(30, 33, 45, .56);
         backdrop-filter: blur(3px);
       }

       .student-report-modal {
         width: min(760px, 100%);
         max-height: calc(100vh - 40px);
         overflow: hidden;
         display: flex;
         flex-direction: column;
         border-radius: 18px;
         background: #fff;
         box-shadow: 0 28px 80px rgba(25, 30, 50, .25);
       }

       .student-report-modal-header {
         display: flex;
         align-items: center;
         justify-content: space-between;
         gap: 15px;
         padding: 17px 19px;
         border-bottom: 1px solid #eceef3;
       }

       .student-report-modal-title {
         display: flex;
         align-items: center;
         gap: 11px;
       }

       .student-report-modal-title > span {
         width: 42px;
         height: 42px;
         display: flex;
         align-items: center;
         justify-content: center;
         border-radius: 11px;
         background: #eef0ff;
         color: #696cff;
         font-size: 20px;
       }

       .student-report-modal-title h3 {
         margin: 0;
         color: #343847;
         font-size: 16px;
         font-weight: 750;
       }

       .student-report-modal-title p {
         margin: 3px 0 0;
         color: #959baa;
         font-size: 10px;
       }

       .student-report-modal-close {
         width: 35px;
         height: 35px;
         border: 1px solid #e3e5eb;
         border-radius: 9px;
         background: #fff;
         color: #767c8b;
         font-size: 18px;
         cursor: pointer;
       }

       .student-report-modal-body {
         min-height: 0;
         padding: 16px 19px 20px;
         overflow-y: auto;
       }

       .student-report-session-note {
         display: flex;
         align-items: center;
         gap: 6px;
         margin-bottom: 10px;
         padding: 8px 10px;
         border: 1px solid #ebe7ff;
         border-radius: 9px;
         background: #faf9ff;
         color: #777e8e;
         font-size: 10px;
       }

       .student-report-session-note i {
         color: #696cff;
         font-size: 15px;
       }

       .student-report-session-note strong {
         color: #565c6c;
       }

       .student-report-search-box {
         position: relative;
       }

       .student-report-search-box > i {
         position: absolute;
         left: 13px;
         top: 50%;
         transform: translateY(-50%);
         color: #8f95a4;
         font-size: 17px;
       }

       .student-report-search-box input {
         width: 100%;
         height: 47px;
         padding: 0 45px 0 41px;
         border: 1px solid #dfe2e9;
         border-radius: 11px;
         outline: none;
         color: #3e4352;
         font-size: 12px;
       }

       .student-report-search-box input:focus {
         border-color: #696cff;
         box-shadow: 0 0 0 4px rgba(105,108,255,.08);
       }

       .student-search-spinner,
       .student-search-clear {
         position: absolute;
         right: 12px;
         top: 50%;
         transform: translateY(-50%);
       }

       .student-search-spinner {
         color: #696cff;
       }

       .student-search-clear {
         border: 0;
         background: transparent;
         color: #999fad;
         font-size: 17px;
         cursor: pointer;
       }

       .student-report-search-help {
         margin: 6px 2px 12px;
         color: #9ca1ae;
         font-size: 9px;
       }

       .student-report-results {
         min-height: 250px;
       }

       .student-result-count {
         display: flex;
         justify-content: space-between;
         gap: 10px;
         margin-bottom: 7px;
         color: #8e94a3;
         font-size: 9px;
       }

       .student-result-count strong {
         color: #5c6271;
       }

       .student-result-list {
         display: flex;
         flex-direction: column;
         gap: 7px;
       }

       .student-result-item {
         width: 100%;
         display: flex;
         align-items: center;
         gap: 10px;
         padding: 10px;
         border: 1px solid #e7e9ef;
         border-radius: 11px;
         background: #fff;
         text-align: left;
         cursor: pointer;
         transition: .18s ease;
       }

       .student-result-item:hover {
         border-color: #cfd1ff;
         background: #fbfbff;
         transform: translateY(-1px);
         box-shadow: 0 6px 16px rgba(40,45,70,.055);
       }

       .student-result-avatar {
         width: 42px;
         height: 42px;
         flex: 0 0 42px;
         display: flex;
         align-items: center;
         justify-content: center;
         border-radius: 10px;
         background: #eef0ff;
         color: #696cff;
         font-size: 11px;
         font-weight: 800;
       }

       .student-result-main {
         min-width: 0;
         flex: 1;
       }

       .student-result-name-row {
         display: flex;
         align-items: center;
         gap: 7px;
       }

       .student-result-name-row strong {
         overflow: hidden;
         color: #3d4251;
         font-size: 11px;
         text-overflow: ellipsis;
         white-space: nowrap;
       }

       .student-result-name-row span {
         padding: 2px 6px;
         border-radius: 5px;
         background: #f1f2f6;
         color: #767c8a;
         font-size: 8px;
         font-weight: 700;
       }

       .student-result-meta {
         display: flex;
         flex-wrap: wrap;
         gap: 4px 10px;
         margin-top: 5px;
       }

       .student-result-meta span {
         display: inline-flex;
         align-items: center;
         gap: 3px;
         color: #9298a7;
         font-size: 8px;
       }

       .student-result-open {
         display: flex;
         align-items: center;
         gap: 4px;
         color: #696cff;
         font-size: 9px;
         font-weight: 750;
       }

       .student-report-empty {
         min-height: 250px;
         display: flex;
         flex-direction: column;
         align-items: center;
         justify-content: center;
         padding: 25px;
         color: #989eac;
         text-align: center;
       }

       .student-report-empty > i {
         width: 50px;
         height: 50px;
         display: flex;
         align-items: center;
         justify-content: center;
         border-radius: 13px;
         background: #f1f2f7;
         color: #888f9f;
         font-size: 22px;
       }

       .student-report-empty strong {
         margin-top: 9px;
         color: #5b6170;
         font-size: 11px;
       }

       .student-report-empty p {
         margin: 4px 0 0;
         font-size: 9px;
       }

       @media (max-width: 640px) {
         .student-report-modal-backdrop {
           padding: 10px;
           align-items: flex-end;
         }

         .student-report-modal {
           max-height: calc(100vh - 20px);
           border-radius: 16px 16px 9px 9px;
         }

         .student-report-modal-header,
         .student-report-modal-body {
           padding-left: 13px;
           padding-right: 13px;
         }

         .student-result-open span {
           display: none;
         }
       }


       /* =========================================================
          ATTENDANCE V2: FIX TOP NAVBAR GAP
          The template's .layout-navbar-fixed rule adds a top offset to
          .layout-page. This route already renders a sticky Nav at the top,
          so only this dashboard must cancel that reserved space.
          Scope all overrides to avoid changing other SCAI screens.
       ========================================================= */
       html.layout-navbar-fixed .layout-wrapper.attendance-dashboard .layout-page,
       html.layout-navbar-fixed .attendance-dashboard.layout-wrapper .layout-page,
       .attendance-dashboard.layout-wrapper .layout-page {
         padding-top: 0 !important;
         margin-top: 0 !important;
         min-width: 0;
       }

       .attendance-dashboard.layout-wrapper,
       .attendance-dashboard.layout-wrapper > .layout-container {
         margin-top: 0 !important;
         padding-top: 0 !important;
       }

       .attendance-dashboard #layout-navbar.scai-topbar {
         top: 0 !important;
         margin: 0 !important;
         width: 100% !important;
         max-width: none !important;
         border-radius: 0 !important;
       }

       .attendance-dashboard .layout-page > .no-print:first-child {
         margin: 0 !important;
         padding: 0 !important;
       }

       /* Print layout remains controlled by the existing @media print. */

       /* Keep V2 inside the same application shell as the old dashboard */

        .attendance-dashboard .layout-page {

          min-width: 0;

        }



        .attendance-dashboard .content-wrapper {

          min-width: 0;

        }



        @media print {

          .no-print,

          .layout-menu,

          .layout-navbar {

            display: none !important;

          }



          .attendance-dashboard .layout-page,

          .attendance-dashboard .content-wrapper {

            width: 100% !important;

            margin: 0 !important;

          }

        }



        /* =====================================================



           ROOT



        \\===================================================== */







        .attendance-reporting-page {



          min-height: 100vh;



          background:



            linear-gradient(



              180deg,



              #f7f9fc 0%,



              #f4f6fa 100%



            );



          color: #202534;



        }







        .attendance-navbar-wrapper {



          position: relative;



          z-index: 20;



          background: #ffffff;



        }







        .attendance-master-v2-wrapper {

        width: 100%;

        max-width: 1540px;

        margin: 0 auto;

        padding: 16px 30px 0;

      }



      @media (max-width: 767px) {

        .attendance-master-v2-wrapper {

          padding: 10px 12px 0;

        }

      }



      .attendance-main {



          width: 100%;



          max-width: 1540px;



          margin: 0 auto;



          padding: 28px 30px 50px;



        }











        /* =====================================================



           HEADER



        \\===================================================== */







        .attendance-page-header {



          display: flex;



          justify-content: space-between;



          align-items: center;



          gap: 24px;



          margin-bottom: 24px;



        }







        .attendance-header-left {



          display: flex;



          align-items: center;



          gap: 16px;



          min-width: 0;



        }







        .attendance-header-icon {



          width: 58px;



          height: 58px;



          flex: 0 0 58px;



          border-radius: 17px;



          display: flex;



          align-items: center;



          justify-content: center;



          background:



            linear-gradient(



              135deg,



              #696cff,



              #5360e8



            );



          color: #fff;



          font-size: 28px;



          box-shadow:



            0 10px 24px rgba(105, 108, 255, .22);



        }







        .attendance-breadcrumb {



          display: flex;



          align-items: center;



          gap: 4px;



          color: #8b90a0;



          font-size: 12px;



          font-weight: 600;



          margin-bottom: 3px;



        }







        .attendance-page-header h1 {



          margin: 0;



          color: #25293c;



          font-size: 27px;



          font-weight: 750;



          letter-spacing: -.5px;



        }







        .attendance-page-header p {



          margin: 5px 0 0;



          color: #7c8293;



          font-size: 14px;



        }







        .attendance-header-actions {



          display: flex;



          align-items: center;



          gap: 10px;



        }







        .attendance-header-actions button {



          min-height: 43px;



          border-radius: 10px;



          padding: 0 17px;



          border: 0;



          font-weight: 600;



          font-size: 13px;



          display: inline-flex;



          align-items: center;



          justify-content: center;



          gap: 7px;



          transition: all .2s ease;



        }







        .attendance-refresh-btn {



          background: #ffffff;



          color: #525866;



          border: 1px solid #e2e5eb !important;



        }







        .attendance-refresh-btn:hover {



          border-color: #696cff !important;



          color: #696cff;



        }







        .attendance-mark-btn {



          background: #696cff;



          color: #fff;



          box-shadow:



            0 7px 16px rgba(105,108,255,.20);



        }







        .attendance-mark-btn:hover {



          background: #595cd9;



          transform: translateY(-1px);



        }











        /* =====================================================



           FILTER



        \\===================================================== */







        .attendance-filter-card {



          background: #fff;



          border: 1px solid #e8eaf0;



          border-radius: 15px;



          padding: 16px 18px;



          display: flex;



          align-items: center;



          justify-content: space-between;



          gap: 20px;



          margin-bottom: 27px;



          box-shadow:



            0 3px 14px rgba(32,37,52,.035);



        }







        .attendance-filter-heading {



          display: flex;



          align-items: center;



          gap: 11px;



        }







        .attendance-filter-icon {



          width: 40px;



          height: 40px;



          border-radius: 10px;



          background: #f0f1ff;



          color: #696cff;



          display: flex;



          align-items: center;



          justify-content: center;



          font-size: 19px;



        }







        .attendance-filter-heading strong {



          display: block;



          font-size: 14px;



          color: #353949;



        }







        .attendance-filter-heading small {



          display: block;



          color: #969baa;



          margin-top: 2px;



          font-size: 11px;



        }







        .attendance-filter-controls {



          display: flex;



          gap: 12px;



          align-items: flex-end;



        }







        .attendance-filter-field {



          min-width: 205px;



        }







        .attendance-filter-field label {



          display: block;



          font-size: 11px;



          font-weight: 650;



          color: #707687;



          margin: 0 0 5px 2px;



        }







        .attendance-input-wrapper {



          position: relative;



        }







        .attendance-input-wrapper > i {



          position: absolute;



          left: 12px;



          top: 50%;



          transform: translateY(-50%);



          color: #9398a7;



          font-size: 16px;



          pointer-events: none;



        }







        .attendance-input-wrapper select,



        .attendance-input-wrapper input {



          width: 100%;



          height: 40px;



          border-radius: 9px;



          border: 1px solid #dfe2e9;



          background: #fff;



          color: #424758;



          padding: 0 11px 0 37px;



          font-size: 12px;



          outline: none;



        }







        .attendance-input-wrapper select:focus,



        .attendance-input-wrapper input:focus {



          border-color: #696cff;



          box-shadow:



            0 0 0 3px rgba(105,108,255,.08);



        }











        /* =====================================================



           SECTION HEADING



        \\===================================================== */







        .attendance-section-heading {



          display: flex;



          align-items: center;



          justify-content: space-between;



          margin-bottom: 13px;



        }







        .attendance-section-heading > div {



          display: flex;



          align-items: center;



          gap: 9px;



        }







        .attendance-heading-icon {



          width: 34px;



          height: 34px;



          display: flex;



          align-items: center;



          justify-content: center;



          border-radius: 9px;



          background: #e9f8ef;



          color: #28a964;



          font-size: 17px;



        }







        .attendance-section-heading h2 {



          margin: 0;



          font-size: 16px;



          font-weight: 700;



          color: #343848;



        }







        .attendance-section-heading p {



          margin: 2px 0 0;



          font-size: 11px;



          color: #9499a7;



        }







        .attendance-live-badge {



          display: inline-flex;



          align-items: center;



          gap: 6px;



          background: #edf9f2;



          color: #269b5c;



          padding: 6px 10px;



          border-radius: 20px;



          font-size: 10px;



          font-weight: 700;



        }







        .attendance-live-badge span {



          width: 6px;



          height: 6px;



          background: #32b66d;



          border-radius: 50%;



        }











        /* =====================================================



           STAT CARDS



        \\===================================================== */







        .attendance-summary-grid {



          display: grid;



          grid-template-columns: repeat(4, minmax(0, 1fr));



          gap: 15px;



          margin-bottom: 31px;



        }







        .attendance-stat-card {



          text-align: left;



          width: 100%;



          border: 1px solid #e7e9ef;



          background: #fff;



          border-radius: 14px;



          padding: 18px;



          min-height: 162px;



          transition:



            transform .2s ease,



            box-shadow .2s ease,



            border-color .2s ease;



          position: relative;



          overflow: hidden;



        }







        .attendance-stat-card:not(:disabled) {



          cursor: pointer;



        }







        .attendance-stat-card:disabled {



          opacity: 1;



          cursor: default;



        }







        .attendance-stat-card:not(:disabled):hover {



          transform: translateY(-3px);



          box-shadow:



            0 10px 28px rgba(32,37,52,.08);



        }







        .attendance-stat-card-head {



          display: flex;



          align-items: center;



          justify-content: space-between;



        }







        .attendance-stat-icon {



          width: 41px;



          height: 41px;



          border-radius: 11px;



          display: flex;



          align-items: center;



          justify-content: center;



          font-size: 20px;



        }







        .attendance-stat-percent {



          font-size: 11px;



          font-weight: 700;



          padding: 5px 8px;



          border-radius: 15px;



        }







        .attendance-stat-value {



          font-size: 29px;



          font-weight: 750;



          margin-top: 14px;



          line-height: 1;



          color: #303443;



        }







        .attendance-stat-title {



          margin-top: 8px;



          font-size: 13px;



          font-weight: 700;



          color: #454a5a;



        }







        .attendance-stat-subtitle {



          margin-top: 2px;



          color: #999eac;



          font-size: 10px;



        }







        .attendance-stat-progress {



          height: 4px;



          background: #eef0f4;



          border-radius: 20px;



          overflow: hidden;



          margin-top: 12px;



        }







        .attendance-stat-progress div {



          height: 100%;



          border-radius: 20px;



        }











        /* PRIMARY */







        .attendance-stat-primary



          .attendance-stat-icon {



          background: #eef0ff;



          color: #696cff;



        }







        .attendance-stat-primary {



          border-top: 3px solid #696cff;



        }











        /* SUCCESS */







        .attendance-stat-success



          .attendance-stat-icon {



          background: #eaf8f0;



          color: #28a964;



        }







        .attendance-stat-success



          .attendance-stat-percent {



          background: #eaf8f0;



          color: #269b5c;



        }







        .attendance-stat-success



          .attendance-stat-progress div {



          background: #28a964;



        }







        .attendance-stat-success {



          border-top: 3px solid #28a964;



        }











        /* DANGER */







        .attendance-stat-danger



          .attendance-stat-icon {



          background: #fff0f1;



          color: #ea5455;



        }







        .attendance-stat-danger



          .attendance-stat-percent {



          background: #fff0f1;



          color: #db494b;



        }







        .attendance-stat-danger



          .attendance-stat-progress div {



          background: #ea5455;



        }







        .attendance-stat-danger {



          border-top: 3px solid #ea5455;



        }











        /* WARNING */







        .attendance-stat-warning



          .attendance-stat-icon {



          background: #fff6e8;



          color: #ff9f43;



        }







        .attendance-stat-warning



          .attendance-stat-percent {



          background: #fff6e8;



          color: #e88a31;



        }







        .attendance-stat-warning



          .attendance-stat-progress div {



          background: #ff9f43;



        }







        .attendance-stat-warning {



          border-top: 3px solid #ff9f43;



        }











        /* =====================================================



           REPORT SECTIONS



        \\===================================================== */







        .attendance-reports-section {



          margin-bottom: 31px;



        }







        .attendance-section-title {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 13px;
        }







        .attendance-section-title > div {



          display: flex;



          align-items: center;



          gap: 10px;



        }







        .attendance-section-title-icon {



          width: 36px;



          height: 36px;



          border-radius: 9px;



          display: flex;



          align-items: center;



          justify-content: center;



          font-size: 18px;



        }







        .attendance-section-title-icon.blue {



          background: #edf1ff;



          color: #696cff;



        }







        .attendance-section-title-icon.purple {



          background: #f3ecff;



          color: #8d5ce5;



        }







        .attendance-section-title h2 {



          margin: 0;



          font-size: 16px;



          color: #353949;



          font-weight: 700;



        }







        .attendance-section-title p {



          margin: 2px 0 0;



          color: #969baa;



          font-size: 11px;



        }











        /* =====================================================



           REPORT GRID



        \\===================================================== */







        .attendance-report-grid {



          display: grid;



          grid-template-columns:



            repeat(3, minmax(0, 1fr));



          gap: 15px;



        }







        .attendance-report-grid.primary-grid {



          grid-template-columns:



            repeat(4, minmax(0, 1fr));



        }







        .attendance-report-card {



          width: 100%;



          min-height: 205px;



          padding: 17px;



          background: #fff;



          border: 1px solid #e6e8ee;



          border-radius: 14px;



          text-align: left;



          display: flex;



          flex-direction: column;



          transition:



            transform .2s ease,



            box-shadow .2s ease,



            border-color .2s ease;



        }







        .attendance-report-card:hover {



          transform: translateY(-3px);



          border-color: #d7daf0;



          box-shadow:



            0 10px 30px rgba(31,36,50,.075);



        }







        .attendance-report-card-top {



          display: flex;



          align-items: flex-start;



          justify-content: space-between;



          gap: 10px;



        }







        .attendance-report-icon {



          width: 45px;



          height: 45px;



          border-radius: 12px;



          display: flex;



          align-items: center;



          justify-content: center;



          font-size: 22px;



        }







        .attendance-report-tag {



          padding: 4px 8px;



          border-radius: 15px;



          background: #f5f6f8;



          color: #8c919f;



          font-size: 9px;



          font-weight: 700;



          text-transform: uppercase;



          letter-spacing: .3px;



        }







        .attendance-report-content {



          flex: 1;



        }







        .attendance-report-content h5 {



          margin: 15px 0 6px;



          color: #343848;



          font-size: 14px;



          font-weight: 700;



        }







        .attendance-report-content p {



          margin: 0;



          color: #9297a6;



          font-size: 10.5px;



          line-height: 1.6;



        }







        .attendance-report-footer {



          display: flex;



          align-items: center;



          justify-content: space-between;



          margin-top: 14px;



          padding-top: 11px;



          border-top: 1px solid #f0f1f4;



          color: #696cff;



          font-size: 10px;



          font-weight: 700;



        }







        .attendance-report-arrow {



          width: 26px;



          height: 26px;



          border-radius: 7px;



          background: #f0f1ff;



          display: flex;



          align-items: center;



          justify-content: center;



          transition: all .2s ease;



        }







        .attendance-report-card:hover



          .attendance-report-arrow {



          background: #696cff;



          color: #fff;



        }











        /* =====================================================



           REPORT ICON COLORS



        \\===================================================== */







        .report-icon-blue {



          background: #edf1ff;



          color: #5c67e8;



        }







        .report-icon-purple {



          background: #f4edff;



          color: #8a5bd8;



        }







        .report-icon-indigo {



          background: #eceeff;



          color: #575fcf;



        }







        .report-icon-green {



          background: #eaf8f0;



          color: #269b5c;



        }







        .report-icon-red {



          background: #fff0f1;



          color: #e84d50;



        }







        .report-icon-cyan {



          background: #e8f8fb;



          color: #18a6b8;



        }







        .report-icon-orange {



          background: #fff4e9;



          color: #ee8b2e;



        }







        .report-icon-violet {



          background: #f3edff;



          color: #8456d8;



        }







        .report-icon-yellow {



          background: #fff8df;



          color: #d69b18;



        }











        /* =====================================================



           BOTTOM BANNER



        \\===================================================== */







        .attendance-bottom-banner {



          background:



            linear-gradient(



              135deg,



              #6265ef,



              #7578ff



            );



          border-radius: 16px;



          padding: 19px 21px;



          display: flex;



          align-items: center;



          gap: 15px;



          color: #fff;



          box-shadow:



            0 10px 28px rgba(98,101,239,.18);



        }







        .attendance-bottom-banner-icon {



          width: 47px;



          height: 47px;



          flex: 0 0 47px;



          border-radius: 12px;



          display: flex;



          align-items: center;



          justify-content: center;



          background: rgba(255,255,255,.15);



          font-size: 23px;



        }







        .attendance-bottom-banner-content {



          flex: 1;



          min-width: 0;



        }







        .attendance-bottom-banner h4 {



          color: #fff;



          margin: 0 0 3px;



          font-size: 14px;



          font-weight: 700;



        }







        .attendance-bottom-banner p {



          margin: 0;



          color: rgba(255,255,255,.78);



          font-size: 10.5px;



        }







        .attendance-bottom-banner button {



          border: 0;



          background: #fff;



          color: #5d60e1;



          min-height: 39px;



          border-radius: 9px;



          padding: 0 15px;



          display: inline-flex;



          align-items: center;



          justify-content: center;



          gap: 7px;



          font-size: 10.5px;



          font-weight: 700;



          white-space: nowrap;



          transition: transform .2s ease;



        }







        .attendance-bottom-banner button:hover {



          transform: translateY(-1px);



        }











        /* =====================================================



           LOADING



        \\===================================================== */







        .attendance-spin {



          animation: attendance-spin 1s linear infinite;



        }







        @keyframes attendance-spin {



          from {



            transform: rotate(0deg);



          }







          to {



            transform: rotate(360deg);



          }



        }











        /* =====================================================



           LARGE TABLET



        \\===================================================== */







        @media (max-width: 1199px) {







          .attendance-main {



            padding: 24px 22px 45px;



          }







          .attendance-summary-grid {



            grid-template-columns:



              repeat(2, minmax(0, 1fr));



          }







          .attendance-report-grid,



          .attendance-report-grid.primary-grid {



            grid-template-columns:



              repeat(2, minmax(0, 1fr));



          }







        }











        /* =====================================================



           TABLET



        \\===================================================== */







        @media (max-width: 991px) {







          .attendance-page-header {



            align-items: flex-start;



          }







          .attendance-filter-card {



            align-items: flex-start;



            flex-direction: column;



          }







          .attendance-filter-controls {



            width: 100%;



          }







          .attendance-filter-field {



            flex: 1;



          }







        }











        /* =====================================================



           MOBILE



        \\===================================================== */







        @media (max-width: 767px) {







          .attendance-main {



            padding: 17px 13px 35px;



          }







          .attendance-page-header {



            flex-direction: column;



          }







          .attendance-header-left {



            align-items: flex-start;



          }







          .attendance-header-icon {



            width: 48px;



            height: 48px;



            flex-basis: 48px;



            border-radius: 13px;



            font-size: 23px;



          }







          .attendance-page-header h1 {



            font-size: 20px;



          }







          .attendance-page-header p {



            font-size: 11px;



          }







          .attendance-header-actions {



            width: 100%;



          }







          .attendance-header-actions button {



            flex: 1;



          }







          .attendance-filter-card {



            padding: 14px;



          }







          .attendance-filter-controls {



            flex-direction: column;



          }







          .attendance-filter-field {



            width: 100%;



            min-width: 0;



          }







          .attendance-summary-grid {



            grid-template-columns:



              repeat(2, minmax(0, 1fr));



            gap: 10px;



          }







          .attendance-stat-card {



            padding: 14px;



            min-height: 150px;



          }







          .attendance-stat-value {



            font-size: 25px;



          }







          .attendance-report-grid,



          .attendance-report-grid.primary-grid {



            grid-template-columns: 1fr;



            gap: 10px;



          }







          .attendance-report-card {



            min-height: 185px;



          }







          .attendance-bottom-banner {



            align-items: flex-start;



            flex-wrap: wrap;



          }







          .attendance-bottom-banner button {



            width: 100%;



          }







        }











        /* =====================================================



           SMALL MOBILE



        \\===================================================== */







        @media (max-width: 430px) {







          .attendance-main {



            padding-left: 10px;



            padding-right: 10px;



          }







          .attendance-header-actions {



            flex-direction: column;



          }







          .attendance-header-actions button {



            width: 100%;



          }







          .attendance-summary-grid {



            grid-template-columns: 1fr 1fr;



            gap: 8px;



          }







          .attendance-stat-card {



            padding: 12px;



          }







          .attendance-stat-icon {



            width: 36px;



            height: 36px;



            font-size: 18px;



          }







          .attendance-stat-value {



            font-size: 23px;



          }







          .attendance-stat-title {



            font-size: 11px;



          }







          .attendance-stat-subtitle {



            font-size: 9px;



          }







          .attendance-section-heading {



            align-items: flex-start;



          }







          .attendance-live-badge {



            display: none;



          }







        }








/* ===== Compact V2 dashboard: refined report menu cards ===== */
.attendance-reporting-page { background: #f7f8fc; padding: 16px 0 24px; }
.attendance-reporting-page .attendance-reports-section { margin-bottom: 22px; }
.attendance-reporting-page .attendance-report-grid,
.attendance-reporting-page .attendance-report-grid.primary-grid {
  display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 11px;
}
.attendance-reporting-page .attendance-report-card {
  min-width: 0; min-height: 0; height: 152px; padding: 13px 14px 11px;
  border-radius: 12px; border: 1px solid #e6e9f1; background: #fff;
  box-shadow: 0 2px 9px rgba(29, 36, 65, .025);
  display: flex; flex-direction: column; text-align: left;
  transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease;
}
.attendance-reporting-page .attendance-report-card:hover {
  border-color: #c7caff; box-shadow: 0 7px 20px rgba(68, 70, 125, .085);
  transform: translateY(-2px);
}
.attendance-reporting-page .attendance-report-card:focus-visible {
  outline: 3px solid rgba(105,108,255,.25); outline-offset: 2px;
}
.attendance-reporting-page .attendance-report-card-top { align-items: center; gap: 8px; }
.attendance-reporting-page .attendance-report-icon {
  width: 34px; height: 34px; flex: 0 0 34px; border-radius: 9px; font-size: 17px;
}
.attendance-reporting-page .attendance-report-tag {
  padding: 3px 7px; border-radius: 6px; font-size: 9px; line-height: 1.3;
}
.attendance-reporting-page .attendance-report-content { min-width: 0; flex: 1; }
.attendance-reporting-page .attendance-report-content h5 {
  margin: 10px 0 4px; font-size: 12px; font-weight: 750; line-height: 1.35;
  color: #34394d; overflow-wrap: anywhere;
}
.attendance-reporting-page .attendance-report-content p {
  margin: 0; font-size: 10px; line-height: 1.4; color: #8a90a0;
  display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2;
  line-clamp: 2; overflow: hidden;
}
.attendance-reporting-page .attendance-report-footer {
  margin-top: 8px; padding-top: 8px; border-top: 1px solid #f0f1f5;
  font-size: 10px; min-height: 23px;
}
.attendance-reporting-page .attendance-report-arrow {
  width: 23px; height: 23px; border-radius: 7px; font-size: 13px;
}
.attendance-reporting-page .attendance-summary-grid { gap: 11px; margin-bottom: 22px; }
.attendance-reporting-page .attendance-stat-card {
  min-height: 125px; padding: 13px 14px; border-radius: 12px;
}
.attendance-reporting-page .attendance-stat-icon { width: 35px; height: 35px; border-radius: 9px; font-size: 17px; }
.attendance-reporting-page .attendance-section-title { margin-bottom: 11px; }
.attendance-reporting-page .attendance-section-title h2 { font-size: 15px; }
@media (max-width: 1250px) {
  .attendance-reporting-page .attendance-report-grid,
  .attendance-reporting-page .attendance-report-grid.primary-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 850px) {
  .attendance-reporting-page .attendance-report-grid,
  .attendance-reporting-page .attendance-report-grid.primary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .attendance-reporting-page .attendance-summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 480px) {
  .attendance-reporting-page { padding-top: 12px; }
  .attendance-reporting-page .attendance-report-grid,
  .attendance-reporting-page .attendance-report-grid.primary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
  .attendance-reporting-page .attendance-report-card { height: 158px; padding: 10px; border-radius: 10px; }
  .attendance-reporting-page .attendance-report-icon { width: 30px; height: 30px; flex-basis: 30px; font-size: 15px; }
  .attendance-reporting-page .attendance-report-content h5 { font-size: 11px; margin-top: 9px; }
  .attendance-reporting-page .attendance-report-content p { font-size: 9px; }
  .attendance-reporting-page .attendance-report-tag { font-size: 8px; }
  .attendance-reporting-page .attendance-stat-card { min-height: 115px; padding: 11px; }
}
@media (max-width: 350px) {
  .attendance-reporting-page .attendance-report-grid,
  .attendance-reporting-page .attendance-report-grid.primary-grid { grid-template-columns: 1fr; }
  .attendance-reporting-page .attendance-report-card { height: 136px; }
}
@media (prefers-reduced-motion: reduce) {
  .attendance-reporting-page .attendance-report-card { transition: none; }
  .attendance-reporting-page .attendance-report-card:hover { transform: none; }
}

        `}
      </style>
    </>
  );
}
